/* global globalThis */
import { ErsStreamSession } from './ersStreamSession'
import { recordingError } from './recordingErrors'

const defaultClock = { now: () => Date.now(), setInterval: (...args) => setInterval(...args), clearInterval: id => clearInterval(id) }

export function normalizeChatErsResult (value) {
  if (typeof value?.Text !== 'string') throw recordingError('ERS_RESULT_INVALID')
  if (!value.Text.trim()) throw recordingError('NO_SPEECH')
  if (typeof value.Id !== 'string' || !value.Id.trim()) throw recordingError('ERS_RESULT_INVALID')
  let audioUrl = ''
  try {
    const url = new URL(value.AudioUrl)
    if (url.protocol === 'https:') audioUrl = url.href
  } catch { /* A missing replay URL does not discard a successful transcript. */ }
  // Preserve the server's ERS ID verbatim for the existing chat audio field.
  // Backend acceptance of this ID must be checked in the integration environment.
  return {
    result: value.Text.trim(),
    id: value.Id,
    assessment: {
      score: typeof value.Score === 'number' && Number.isFinite(value.Score) ? value.Score : null,
      audioUrl,
      audioTruncated: value.AudioTruncated === true
    }
  }
}

// Free answers send an empty reference, never the AI's question or an invented answer.
// The ERS server must support empty-reference sessions for this conversation flow.
export class ChatSpeechSession {
  constructor ({ getToken, lang = 'en-US', onState = () => {}, onCountdown = () => {}, onTranscript = () => {}, onResult = () => {}, onError = () => {}, createStreamSession, platform = globalThis, clock = defaultClock }) {
    Object.assign(this, { lang, onState, onCountdown, onTranscript, onResult, onError, platform, clock })
    this.state = 'idle'
    this.ended = false
    this.heardSpeech = false
    this.limitSeconds = 16
    this.streamSession = (createStreamSession || (options => new ErsStreamSession(options)))({
      getToken,
      lang,
      referenceText: '',
      platform,
      onStream: stream => this.monitorMicrophone(stream),
      onState: value => {
        if (this.ended || value === 'complete') return
        if (value === 'recording') {
          this.startedAt = this.lastSpeechAt = this.clock.now()
          this.onCountdown(this.limitSeconds)
          this.timer = this.clock.setInterval(() => this.tick(), 250)
        } else this.clock.clearInterval(this.timer)
        if (value === 'finishing') this.releaseMonitor()
        this.setState(value)
      },
      onTranscript: value => { if (!this.ended) this.onTranscript(value) },
      onComplete: value => this.complete(value),
      onError: error => this.fail(error)
    })
  }

  setState (value) { this.state = value; this.onState(value) }

  start () {
    if (this.state !== 'idle' || this.ended) return
    return this.streamSession.start()
  }

  monitorMicrophone (stream) {
    if (this.ended) return
    // Observe the same stream as MediaRecorder, without opening a second microphone.
    const AudioContext = this.platform.AudioContext || this.platform.webkitAudioContext
    if (!AudioContext) return
    try {
      this.context = new AudioContext()
      this.source = this.context.createMediaStreamSource(stream)
      this.analyser = this.context.createAnalyser()
      this.analyser.fftSize = 256
      this.samples = new Uint8Array(this.analyser.fftSize)
      this.source.connect(this.analyser)
      this.context.resume().catch(() => {})
    } catch { this.releaseMonitor() }
  }

  getWaveform () {
    if (!this.analyser || this.context?.state !== 'running') return new Uint8Array(0)
    this.analyser.getByteTimeDomainData(this.samples)
    return this.samples
  }

  tick () {
    if (this.state !== 'recording' || this.ended) return
    const now = this.clock.now()
    const remaining = Math.max(0, this.limitSeconds - Math.floor((now - this.startedAt) / 1000))
    this.onCountdown(remaining)
    const samples = this.getWaveform()
    const energy = samples.reduce((sum, sample) => sum + ((sample - 128) / 128) ** 2, 0)
    if (samples.length && Math.sqrt(energy / samples.length) > 0.02) {
      this.heardSpeech = true
      this.lastSpeechAt = now
    }
    if (!remaining || (samples.length && this.heardSpeech && now - this.lastSpeechAt >= 3000)) this.stop()
  }

  stop () {
    if (this.state === 'recording' && !this.ended) this.streamSession.stop()
  }

  complete (value) {
    if (this.ended) return
    let result
    try { result = normalizeChatErsResult(value) } catch (error) { return this.fail(error) }
    this.ended = true
    this.cleanup()
    this.setState('complete')
    this.onResult(result)
  }

  releaseMonitor () {
    if (this.source) this.source.disconnect()
    if (this.analyser) this.analyser.disconnect()
    if (this.context && this.context.state !== 'closed') this.context.close().catch(() => {})
    this.source = this.analyser = this.context = this.samples = null
  }

  cleanup () {
    this.clock.clearInterval(this.timer)
    this.releaseMonitor()
    this.streamSession.cancel()
  }

  fail (error) {
    if (this.ended) return
    this.ended = true
    this.cleanup()
    this.setState('error')
    this.onError(error)
  }

  cancel () {
    if (this.ended) return
    this.ended = true
    this.cleanup()
    this.setState('idle')
  }
}
